'use client';

import NextLink from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { styled } from '@mui/material/styles';
import { useAdminGuard } from '@/hooks/useAdminGuard';
import { Box, Grid, Stack, Typography } from '@/primitives';
import { APP_ROUTE_PATHS, PUBLIC_ROUTES } from '@/configs/routes';
import { blue, slate } from '@/lib/colours';
import { GetStartedPreview } from './GetStartedPreview';
import { FadeInWhenVisible } from './FadeInWhenVisible';
import {
  landingGetStartedCards,
  landingGetStartedSection,
} from './homeLandingContent';
import { darkSectionBg, sectionShell } from './homeLandingSectionSx';

const GetStartedCardLink = styled(NextLink)(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  height: '100%',
  textDecoration: 'none',
  borderRadius: '16px',
  '&:focus-visible': {
    outline: `2px solid ${blue[400]}`,
    outlineOffset: 3,
  },
}));

export function LandingGetStarted() {
  const { handleClickLink } = useAdminGuard(APP_ROUTE_PATHS.DASHBOARD);

  return (
    <Box
      component="section"
      aria-labelledby="landing-get-started-title"
      sx={{
        py: { xs: 8, md: 10 },
        ...darkSectionBg,
      }}
    >
      <Box sx={{ ...sectionShell, position: 'relative', zIndex: 1 }}>
        <FadeInWhenVisible>
          <Stack
            alignItems="center"
            spacing={1}
            sx={{ mb: { xs: 4, md: 6 }, textAlign: 'center', width: '100%' }}
          >
            <Typography
              color={blue[300]}
              sx={{
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.75rem',
                textAlign: 'center',
              }}
              variant="labelmedium"
            >
              {landingGetStartedSection.eyebrow}
            </Typography>
            <Typography
              color={slate[50]}
              component="h2"
              id="landing-get-started-title"
              sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}
              variant="headlinelarge"
            >
              {landingGetStartedSection.title}
            </Typography>
            <Typography
              color={slate[400]}
              sx={{ maxWidth: 520, mx: 'auto', mt: 1, textAlign: 'center' }}
              variant="bodylarge"
            >
              {landingGetStartedSection.subtitle}
            </Typography>
          </Stack>
        </FadeInWhenVisible>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {landingGetStartedCards.map((card, index) => {
            const isSearch = card.id === 'search';
            const href = isSearch
              ? PUBLIC_ROUTES.SEARCH
              : PUBLIC_ROUTES.REGISTRY_LANDING;
            const onClick = isSearch ? undefined : handleClickLink;

            return (
              <Grid
                key={card.id}
                size={{ xs: 12, md: 6 }}
                sx={{ display: 'flex' }}
              >
                <FadeInWhenVisible delay={index * 0.08} fillParent>
                  <GetStartedCardLink
                    aria-describedby={`get-started-${card.id}-desc`}
                    aria-labelledby={`get-started-${card.id}-title`}
                    href={href}
                    onClick={onClick}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        height: '100%',
                        borderRadius: '16px',
                        border: `1px solid ${slate[700]}`,
                        backgroundColor: 'transparent',
                        overflow: 'hidden',
                        transition: 'border-color 0.2s, transform 0.2s',
                        '&:hover': {
                          borderColor: blue[500],
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          px: { xs: 1.5, md: 2 },
                          pt: { xs: 1.5, md: 2 },
                          backgroundColor: 'transparent',
                        }}
                      >
                        <GetStartedPreview cardId={card.id} expandToFill />
                      </Box>
                      <Stack
                        spacing={1}
                        sx={{
                          flexShrink: 0,
                          px: { xs: 2, md: 2.5 },
                          pb: 3,
                          pt: 1.5,
                          borderTop: `1px solid ${slate[700]}`,
                          backgroundColor: slate[900],
                        }}
                      >
                        <Stack alignItems="center" direction="row" spacing={1}>
                          <Typography
                            color={slate[50]}
                            component="h3"
                            id={`get-started-${card.id}-title`}
                            variant="headlinesmall"
                          >
                            {card.headline}
                          </Typography>
                          <ArrowForwardIcon
                            aria-hidden
                            sx={{ color: blue[400], fontSize: 22 }}
                          />
                        </Stack>
                        <Typography
                          color={slate[400]}
                          id={`get-started-${card.id}-desc`}
                          variant="bodymedium"
                        >
                          {card.tagline}
                        </Typography>
                      </Stack>
                    </Box>
                  </GetStartedCardLink>
                </FadeInWhenVisible>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}
